import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  HiOutlineBuildingStorefront, HiOutlinePlusCircle, HiOutlinePencil,
  HiOutlineTrash, HiOutlineMapPin, HiOutlineArrowLeftCircle
} from 'react-icons/hi2';
import api from '../utils/api';
import TopBar from '../components/TopBar';
import { useRestaurant } from '../context/RestaurantContext';

const RestaurantManagement = () => {
  const nav = useNavigate();
  const { refreshRestaurants } = useRestaurant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/restaurants');
      setItems(r.data.restaurants || []);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    try {
      await api.delete(`/restaurants/${confirmDel._id}`);
      toast.success('Deleted');
      setConfirmDel(null);
      await load();
      await refreshRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cannot delete');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title="Restaurants" />
      <div className="page">
        <div className="page-container">
          <div className="page-header">
            <div>
              <button className="btn btn-ghost btn-sm" onClick={() => nav('/restaurants')}>
                <HiOutlineArrowLeftCircle size={16} /> Back
              </button>
              <h1 className="page-title" style={{ marginTop: 8 }}>Restaurants</h1>
              <p className="page-subtitle">Add, edit, or remove your restaurant locations</p>
            </div>
            <button className="btn btn-primary" onClick={() => nav('/restaurants/new')}>
              <HiOutlinePlusCircle size={18} /> Add restaurant
            </button>
          </div>

          {loading ? (
            <div className="empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 48 }}>
              <div className="empty-icon">🏪</div>
              <div className="empty-title">No restaurants yet</div>
              <p className="empty-text">Get started by adding your first restaurant.</p>
              <button className="btn btn-primary" style={{ marginTop: 16 }}
                onClick={() => nav('/restaurants/new')}>
                <HiOutlinePlusCircle size={18} /> Add your first restaurant
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2" style={{ gap: 18 }}>
              {items.map(r => (
                <div key={r._id} className="card" style={{ display: 'flex', gap: 16, padding: 18 }}>
                  <div style={{
                    width: 80, height: 80, flexShrink: 0,
                    borderRadius: 12,
                    background: r.logo ? `url(${r.logo}) center/cover` : 'linear-gradient(135deg, var(--primary-700), var(--primary-500))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {!r.logo && <HiOutlineBuildingStorefront size={32} color="#fff" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{r.name}</h3>
                        {r.location && (
                          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <HiOutlineMapPin size={12} /> {r.location}
                          </p>
                        )}
                        {r.address && <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-light)' }}>{r.address}</p>}
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => nav(`/restaurants/${r._id}/edit`)}>
                        <HiOutlinePencil size={14} /> Edit
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setConfirmDel(r)}>
                        <HiOutlineTrash size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirm delete — kept as modal since it's just a small confirmation */}
      {confirmDel && (
        <div className="modal-overlay" onClick={() => setConfirmDel(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Delete restaurant?</h2>
            <p className="modal-text">
              This will permanently delete <b>{confirmDel.name}</b>.
              You can only delete restaurants with no employees or users linked.
            </p>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantManagement;